import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowDown, ArrowRight, ArrowUp, ArrowUpRight, Check, CircuitBoard, ClipboardCheck, CookingPot, Copy, FileText, Heater, House, Lamp, Lightbulb, Loader2, Maximize2, Minimize2,
  Phone, PhoneCall, PlugZap, RotateCcw, Siren, Square, ThumbsDown, ThumbsUp, Wrench, X, CarFront,
} from "lucide-react";
import { API, api, errMsg } from "@/lib/api";
import { COMPANY, PRICES } from "@/lib/content";
import { EASE } from "@/components/public/Reveal";
import { Mark } from "@/components/public/Logo";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   "Spør oss" – the public assistant.

   One identity (the mark, once, in the header). Replies stream token by
   token with Markdown; tool activity is shown while it happens; a
   receipt appears when an inquiry has been registered. After each reply:
   tap-to-ask follow-ups from the model, plus contextual actions derived
   from the price list (order this service, call, continue in the form).
   ------------------------------------------------------------------ */

const KEY = "ke_chat_v4";
const TEASE_KEY = "ke_chat_teased";
const SUGGESTIONS = [
  { icon: PlugZap, label: "Kva kostar heimeladar montert?", text: "Kva kostar ein heimeladar montert?" },
  { icon: House, label: "Eg treng elektrikar heime", text: "Eg treng elektrikar heime" },
  { icon: CircuitBoard, label: "Sikringane går heile tida", text: "Sikringane går heile tida. Kva kan det vere?" },
  { icon: PhoneCall, label: "Korleis kjem eg i kontakt?", text: "Korleis kjem eg i kontakt med dykk?" },
];
const STEP_LABEL = {
  create_inquiry: { start: "Registrerer førespurnad", ok: "Førespurnad registrert", fail: "Fekk ikkje registrert førespurnaden" },
};
const SERVICE_ICONS = { heimeladar: PlugZap, "lading-sameige": CarFront, sikringsskap: CircuitBoard, "el-kontroll": ClipboardCheck, komfyrvakt: CookingPot, varmekablar: Heater, downlights: Lightbulb, utelys: Lamp, servicebesok: Wrench, vakt: Siren, serviceavtale: FileText };
/* Words that tie a reply to a service in the price list (lower-case substring). Deliberately strict:
   a reply about a charger that mentions "avstand til sikringsskap" must not suggest a new fuse board. */
const SERVICE_WORDS = {
  heimeladar: ["heimeladar", "heimelading", "elbilladar", "ladeboks"],
  "lading-sameige": ["sameige", "ladeanlegg", "burettslag"],
  sikringsskap: ["nytt sikringsskap", "skifte sikringsskap", "utskifting av sikringsskap", "byte sikringsskap", "bytte sikringsskap", "sikringsskapet ditt bør"],
  "el-kontroll": ["el-kontroll", "elkontroll", "el-sjekk", "elsjekk"],
  komfyrvakt: ["komfyrvakt"],
  varmekablar: ["varmekabl", "golvvarme"],
  downlights: ["downlight"],
  utelys: ["utelys", "stikk ute", "utebelysning"],
  servicebesok: ["servicebesøk", "timepris", "første time"],
  vakt: ["utanom arbeidstid", "utrykking"],
  serviceavtale: ["serviceavtale", "internkontroll"],
};
const priceDigits = (p) => (p.price.match(/\d[\d\s]*\d/) || [""])[0].trim();
const ICON = { strokeWidth: 1.5, absoluteStrokeWidth: true, "aria-hidden": true };
const SPRING = { type: "spring", stiffness: 420, damping: 34, mass: 0.8 };
const GAP_MS = 15 * 60 * 1000;

const newId = () => (crypto?.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
const nowISO = () => new Date().toISOString();

function load() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw);
      s.messages = (s.messages || []).map((m) => ({ ...m, pending: false }));
      return s;
    }
  } catch { /* ignore */ }
  return { session_id: newId(), messages: [] };
}

const pad = (n) => String(n).padStart(2, "0");
const MONTHS = ["jan.", "feb.", "mars", "apr.", "mai", "juni", "juli", "aug.", "sep.", "okt.", "nov.", "des."];
function timeLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const t = `${pad(d.getHours())}.${pad(d.getMinutes())}`;
  return d.toDateString() === new Date().toDateString() ? `I dag · ${t}` : `${d.getDate()}. ${MONTHS[d.getMonth()]} · ${t}`;
}
function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "God natt.";
  if (h < 10) return "God morgon.";
  if (h < 18) return "God dag.";
  return "God kveld.";
}

/* Phone numbers and e-mail addresses in the reply become links before Markdown renders. */
function linkify(text) {
  return (text || "")
    .replace(/(?<![\w([/+])(\d{2}\s?\d{2}\s?\d{2}\s?\d{2})(?![\w)\]])/g, (m) => `[${m}](tel:+47${m.replace(/\s/g, "")})`)
    .replace(/(?<![\w([/@])([\w.+-]+@[\w-]+\.[a-z]{2,}(?:\.[a-z]{2,})?)(?![\w)\]])/gi, (m) => `[${m}](mailto:${m})`);
}

/* Services the reply talks about, in price-list order. */
function detectServices(text) {
  const t = (text || "").toLowerCase();
  const found = [];
  for (const p of PRICES) {
    const words = SERVICE_WORDS[p.id] || [p.name.toLowerCase()];
    const digits = priceDigits(p);
    if (words.some((w) => t.includes(w)) || (digits.length >= 4 && t.includes(digits))) found.push(p);
  }
  return found;
}

const MdLink = ({ href = "", children, ...rest }) => {
  const ext = /^https?:/i.test(href);
  return <a href={href} {...(ext ? { target: "_blank", rel: "noreferrer" } : {})} className="u-link font-medium text-foreground" {...rest}>{children}</a>;
};

const Reply = ({ text, streaming }) => (
  <div className={cn("md chat-md", streaming && "stream-caret")}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MdLink }}>{linkify(text)}</ReactMarkdown>
  </div>
);

/* ---------- Streaming transport (SSE over fetch) ---------- */
async function streamChat({ body, signal, onEvent }) {
  const res = await fetch(`${API}/public/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    const err = new Error("stream unavailable");
    err.status = res.status;
    throw err;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  const flush = (chunk) => {
    for (const line of chunk.split("\n")) {
      if (!line.startsWith("data: ")) continue;
      try { onEvent(JSON.parse(line.slice(6))); } catch { /* skip malformed */ }
    }
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      flush(buf.slice(0, idx));
      buf = buf.slice(idx + 2);
    }
  }
  if (buf.trim()) flush(buf);
}

const upsertStep = (steps, ev) => [...(steps || []).filter((s) => s.id !== ev.id), { id: ev.id, name: ev.name, phase: ev.phase, ok: ev.ok }];

/* ---------- Small pieces ---------- */
const IconButton = ({ className, children, ...props }) => (
  <button
    type="button"
    className={cn("inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-[background-color,color,transform] duration-150 hover:bg-secondary hover:text-foreground active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)}
    {...props}
  >
    {children}
  </button>
);

const Chip = ({ children, onClick, to, icon: Icon, tone = "default", testid, className }) => {
  const cls = cn(
    "inline-flex h-9 max-w-full items-center gap-1.5 rounded-full border px-3.5 text-[13.5px] font-medium transition-[background-color,border-color,color,transform] duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    tone === "brand" ? "border-brand bg-brand text-on-brand hover:bg-brand-dark" : "border-border bg-background text-foreground/85 hover:border-foreground/50 hover:text-foreground",
    className
  );
  const inner = (<>{Icon && <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden />}<span className="truncate">{children}</span></>);
  if (to) return <a href={to} onClick={onClick} className={cls} data-testid={testid}>{inner}</a>;
  return <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
};

const TimeDivider = ({ label }) => (
  <div className="flex items-center gap-3 py-1" aria-hidden>
    <span className="h-px flex-1 bg-border/70" />
    <span className="tabular text-[11.5px] text-muted-foreground">{label}</span>
    <span className="h-px flex-1 bg-border/70" />
  </div>
);

const Step = ({ step }) => {
  const l = STEP_LABEL[step.name] || { start: "Arbeider", ok: "Ferdig", fail: "Feila" };
  const running = step.phase === "start";
  const label = running ? l.start : step.ok ? l.ok : l.fail;
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-2.5 py-1 text-[12.5px] font-medium text-foreground/80" data-testid="chat-step" data-phase={step.phase}>
      {running ? <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" {...ICON} /> : <Check className={cn("h-3.5 w-3.5", step.ok ? "text-brand" : "text-muted-foreground")} strokeWidth={2.25} aria-hidden />}
      {label}{running && <span className="sr-only"> …</span>}
    </span>
  );
};

const InquiryReceipt = ({ inquiry }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(inquiry.ref); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* ignore */ }
  };
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: EASE }} className="mt-3 overflow-hidden rounded-2xl border border-border bg-card shadow-card" data-testid="chat-inquiry-card">
      <div className="grain relative bg-ink px-4 py-3.5 text-white">
        <div className="relative flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="t-mono text-white/60">Førespurnad motteken</p>
            <p className="t-num mt-1 text-[1.45rem] leading-none" data-testid="chat-inquiry-ref">{inquiry.ref}</p>
          </div>
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-on-brand"><Check className="h-4 w-4" strokeWidth={2.5} aria-hidden /></span>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <p className="text-[13px] leading-snug text-muted-foreground">Vi tek kontakt på telefon eller e-post for å avklare jobben.</p>
        <button type="button" onClick={copy} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 text-[12.5px] font-medium text-foreground/80 transition-colors duration-150 hover:border-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="chat-copy-ref">
          {copied ? <Check className="h-3.5 w-3.5 text-brand" strokeWidth={2.25} aria-hidden /> : <Copy className="h-3.5 w-3.5" {...ICON} />}
          {copied ? "Kopiert" : "Kopier"}
        </button>
      </div>
    </motion.div>
  );
};

/* A single service the reply is about: price example + order shortcut. */
const ServiceCard = ({ item, onOrder }) => {
  const Icon = SERVICE_ICONS[item.id] || Wrench;
  return (
    <motion.button
      type="button"
      onClick={() => onOrder(item)}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: EASE }}
      className="group mt-3 flex w-full items-center gap-3.5 rounded-2xl border border-border bg-card p-3 pr-3.5 text-left shadow-card transition-[border-color,transform] duration-200 hover:border-foreground/40 active:scale-[0.995] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid="chat-service-card"
    >
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-foreground/75 transition-colors duration-200 group-hover:text-brand"><Icon className="h-5 w-5" {...ICON} /></span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14.5px] font-medium">{item.name}</span>
        <span className="mt-0.5 block text-[12.5px] text-muted-foreground">{item.label || item.unit} · priseksempel</span>
      </span>
      <span className="shrink-0 text-right">
        <span className="t-num block text-[1.1rem] leading-none">{item.price}</span>
        <span className="mt-1 inline-flex items-center gap-1 text-[12px] font-medium text-foreground/70 transition-colors group-hover:text-foreground">Bestill <ArrowRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={2} aria-hidden /></span>
      </span>
    </motion.button>
  );
};

/* Copy + thumbs under a finished reply. */
const TurnActions = ({ text, vote, onVote }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch { /* ignore */ }
  };
  const btn = "inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-[background-color,color,transform] duration-150 hover:bg-secondary hover:text-foreground active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  return (
    <div className="chat-actions mt-1.5 flex items-center gap-0.5" data-open={copied || vote ? "true" : undefined}>
      <button type="button" onClick={copy} className={btn} aria-label="Kopier svaret" title="Kopier" data-testid="chat-copy-message">{copied ? <Check className="h-3.5 w-3.5 text-brand" strokeWidth={2.25} aria-hidden /> : <Copy className="h-3.5 w-3.5" {...ICON} />}</button>
      <button type="button" onClick={() => onVote("up")} className={cn(btn, vote === "up" && "text-brand")} aria-pressed={vote === "up"} aria-label="Godt svar" title="Godt svar" data-testid="chat-feedback-up"><ThumbsUp className={cn("h-3.5 w-3.5", vote === "up" && "fill-current")} {...ICON} /></button>
      <button type="button" onClick={() => onVote("down")} className={cn(btn, vote === "down" && "text-foreground")} aria-pressed={vote === "down"} aria-label="Dårleg svar" title="Dårleg svar" data-testid="chat-feedback-down"><ThumbsDown className={cn("h-3.5 w-3.5", vote === "down" && "fill-current")} {...ICON} /></button>
      <AnimatePresence>{vote && <motion.span key="thanks" initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="ml-1 text-[12px] text-muted-foreground" data-testid="chat-feedback-thanks">Takk for tilbakemeldinga.</motion.span>}</AnimatePresence>
    </div>
  );
};

/* ---------- The widget ---------- */
export const ChatWidget = () => {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [wide, setWide] = useState(false);
  const [tease, setTease] = useState(false);
  const [state, setState] = useState(load);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);
  const abortRef = useRef(null);
  const turnRef = useRef(0);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  /* A quiet nudge the first time, after the visitor has settled in. */
  useEffect(() => {
    let hide;
    const show = setTimeout(() => {
      try {
        if (sessionStorage.getItem(TEASE_KEY) || openRef.current || state.messages.length) return;
        sessionStorage.setItem(TEASE_KEY, "1");
      } catch { /* ignore */ }
      setTease(true);
      hide = setTimeout(() => setTease(false), 12000);
    }, 6000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToBottom = useCallback((smooth) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth && !reduce ? "smooth" : "instant" });
  }, [reduce]);

  useEffect(() => {
    if (open && atBottom) scrollToBottom(!busy);
  }, [state.messages, busy, open, atBottom, scrollToBottom]);

  useEffect(() => {
    if (open) {
      setUnread(false);
      setTease(false);
      setAtBottom(true);
      const t = setTimeout(() => { scrollToBottom(false); inputRef.current?.focus(); }, 80);
      return () => clearTimeout(t);
    }
  }, [open, scrollToBottom]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && openRef.current) {
        setOpen(false);
        setTimeout(() => launcherRef.current?.focus(), 30);
      }
    };
    // Other parts of the site (e.g. /kontakt, /prisar) open the assistant with `window.dispatchEvent(new CustomEvent("ke:open-chat"))`.
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("ke:open-chat", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("ke:open-chat", onOpen);
    };
  }, []);

  // Auto-grow the textarea with its content.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input, open, wide]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 72);
  };

  const patchMsg = (id, fn) => setState((s) => ({ ...s, messages: s.messages.map((m) => (m.id === id ? fn(m) : m)) }));

  /* One turn: optimistic user bubble, streamed reply, graceful fallback to the plain endpoint. */
  const send = async (text, base) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput("");
    const prior = base ?? state.messages;
    const history = prior.filter((m) => m.content && !m.error && !m.stopped).map(({ role, content }) => ({ role, content }));
    const userMsg = { id: newId(), role: "user", content: t, at: nowISO() };
    const asstId = newId();
    setState((s) => ({ ...s, messages: [...prior, userMsg, { id: asstId, role: "assistant", content: "", at: nowISO(), pending: true, steps: [], followups: [] }] }));
    setBusy(true);
    setAtBottom(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const turn = ++turnRef.current;
    const body = { session_id: state.session_id, message: t, history };
    let settled = false;
    try {
      await streamChat({
        body,
        signal: ctrl.signal,
        onEvent: (ev) => {
          if (ev.type === "delta") patchMsg(asstId, (m) => ({ ...m, pending: false, content: m.content + ev.text }));
          else if (ev.type === "tool") patchMsg(asstId, (m) => ({ ...m, pending: false, steps: upsertStep(m.steps, ev) }));
          else if (ev.type === "done") { settled = true; setBusy(false); patchMsg(asstId, (m) => ({ ...m, pending: false, done: true, content: ev.reply || m.content, inquiry: ev.inquiry || null })); }
          else if (ev.type === "suggest") patchMsg(asstId, (m) => ({ ...m, followups: ev.items || [] }));
          else if (ev.type === "error") { settled = true; patchMsg(asstId, (m) => ({ ...m, pending: false, content: ev.partial || m.content, error: ev.message, retry: t })); }
        },
      });
      if (!settled) patchMsg(asstId, (m) => ({ ...m, pending: false, done: true, ...(m.content ? {} : { error: "Svaret kom ikkje fram. Prøv igjen.", retry: t }) }));
    } catch (e) {
      if (e?.name === "AbortError") {
        patchMsg(asstId, (m) => ({ ...m, pending: false, stopped: true, retry: m.content ? undefined : t }));
      } else {
        try {
          const r = await api.post("/public/chat", body);
          patchMsg(asstId, (m) => ({ ...m, pending: false, done: true, content: r.data.reply, inquiry: r.data.inquiry || null }));
        } catch (e2) {
          patchMsg(asstId, (m) => ({ ...m, pending: false, error: errMsg(e2, "Assistenten svarar ikkje akkurat no."), retry: t }));
        }
      }
    } finally {
      // The reply is "done" before follow-ups arrive, so a newer turn may already be running.
      if (turnRef.current === turn) {
        setBusy(false);
        abortRef.current = null;
        setTimeout(() => inputRef.current?.focus(), 30);
      }
      if (!openRef.current) setUnread(true);
    }
  };

  const stop = () => abortRef.current?.abort();

  const retry = (asstId) => {
    const idx = state.messages.findIndex((m) => m.id === asstId);
    const failed = state.messages[idx];
    const userIdx = idx > 0 && state.messages[idx - 1].role === "user" ? idx - 1 : idx;
    send(failed?.retry || state.messages[userIdx]?.content, state.messages.slice(0, userIdx));
  };

  const vote = async (m, value) => {
    const next = m.vote === value ? null : value;
    patchMsg(m.id, (x) => ({ ...x, vote: next }));
    if (!next) return;
    const idx = state.messages.findIndex((x) => x.id === m.id);
    const q = idx > 0 ? state.messages[idx - 1]?.content || "" : "";
    try { await api.post("/public/chat/feedback", { session_id: state.session_id, message_id: m.id, value: next, reply: (m.content || "").slice(0, 4000), question: q.slice(0, 2000) }); } catch { /* best effort */ }
  };

  const reset = () => {
    abortRef.current?.abort();
    setState({ session_id: newId(), messages: [] });
    setInput("");
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => launcherRef.current?.focus(), 30);
  };

  const go = (to) => { setOpen(false); navigate(to); };
  const order = (item) => go(`/kontakt?teneste=${encodeURIComponent(item.id)}`);
  const lastUser = [...state.messages].reverse().find((m) => m.role === "user")?.content || "";
  const formHref = `/kontakt${lastUser ? `?emne=${encodeURIComponent(lastUser.slice(0, 300))}` : ""}`;

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const empty = state.messages.length === 0;
  const canSend = !busy && input.trim().length > 0;
  const lastId = state.messages[state.messages.length - 1]?.id;

  const thread = useMemo(() => {
    const out = [];
    let prev = null;
    state.messages.forEach((m) => {
      const t = new Date(m.at).getTime();
      if (m.role === "user" && (!prev || t - prev > GAP_MS)) out.push({ kind: "time", id: `t-${m.id}`, label: timeLabel(m.at) });
      out.push({ kind: "msg", id: m.id, m });
      prev = t;
    });
    return out;
  }, [state.messages]);

  return (
    <>
      {/* Launcher + teaser */}
      <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3 sm:bottom-6 sm:right-6" data-testid="chat-launcher">
        <AnimatePresence>
          {tease && !open && (
            <motion.div
              key="tease"
              initial={reduce ? false : { opacity: 0, y: 8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98, transition: { duration: 0.14 } }}
              transition={reduce ? { duration: 0 } : SPRING}
              className="shadow-float relative w-[min(300px,calc(100vw-2.5rem))] rounded-2xl border border-border bg-background p-4 pr-10"
              role="status"
              data-testid="chat-teaser"
            >
              <p className="text-[14.5px] font-medium leading-snug">Hei! Lurer du på pris på heimeladar, sikringsskap eller el-kontroll?</p>
              <p className="mt-1 text-[13px] leading-snug text-muted-foreground">Spør assistenten, så svarar han på nynorsk.</p>
              <button type="button" onClick={() => setOpen(true)} className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-full bg-foreground px-3 text-[12.5px] font-medium text-background transition-transform duration-150 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="chat-teaser-open">Spør oss <ArrowRight className="h-3 w-3" strokeWidth={2} aria-hidden /></button>
              <button type="button" onClick={() => setTease(false)} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Lukk" data-testid="chat-teaser-dismiss"><X className="h-3.5 w-3.5" strokeWidth={2} aria-hidden /></button>
              <span aria-hidden className="absolute -bottom-1.5 right-7 h-3 w-3 rotate-45 border-b border-r border-border bg-background" />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {!open && (
            <motion.button
              key="open"
              ref={launcherRef}
              type="button"
              onClick={() => setOpen(true)}
              initial={reduce ? false : { opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.96, transition: { duration: 0.14 } }}
              transition={reduce ? { duration: 0 } : SPRING}
              className={cn("shadow-float relative inline-flex h-12 items-center gap-2.5 rounded-full bg-foreground pl-1.5 pr-5 text-[15px] font-medium text-background transition-transform duration-150 hover:scale-[1.03] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background", (tease || unread) && !reduce && "chat-ring")}
              data-testid="chat-open-button"
              aria-label="Opne assistenten"
              aria-expanded={open}
              aria-controls="ke-assistent"
            >
              <Mark className="h-9 w-9" title="" aria-hidden="true" />
              Spør oss
              {unread && <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-background bg-brand" aria-hidden data-testid="chat-unread-dot" />}
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.section
            key="panel"
            id="ke-assistent"
            role="dialog"
            aria-modal="false"
            aria-label="Assistent frå Kvinnherad Elektro"
            initial={reduce ? false : { opacity: 0, y: 28, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96, transition: { duration: 0.16, ease: "easeIn" } }}
            transition={reduce ? { duration: 0 } : SPRING}
            style={{ transformOrigin: "100% 100%" }}
            className={cn(
              "chat-panel shadow-float fixed inset-0 z-[65] flex flex-col overflow-hidden bg-background sm:inset-auto sm:bottom-6 sm:right-6 sm:rounded-[28px] sm:border sm:border-border/80 sm:transition-[width,height] sm:duration-300 sm:ease-premium",
              wide ? "sm:h-[min(860px,calc(100vh-3rem))] sm:w-[min(760px,calc(100vw-3rem))]" : "sm:h-[min(780px,calc(100vh-4.5rem))] sm:w-[460px]"
            )}
            data-testid="chat-panel"
            data-wide={wide}
          >
            {/* Glass header – the thread scrolls underneath */}
            <header className="chat-glass absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 border-b border-border/60 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-5">
              <div className="flex min-w-0 items-center gap-3">
                <Mark className="h-8 w-8" title="" aria-hidden="true" />
                <div className="min-w-0 leading-tight">
                  <p className="truncate text-[15px] font-medium">{COMPANY.name}</p>
                  <p className="mt-0.5 flex items-center gap-1.5 whitespace-nowrap text-[12.5px] text-muted-foreground" data-testid="chat-status">
                    <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-dot", busy ? "animate-pulse" : "live-dot")} aria-hidden />
                    {busy ? "Skriv svar …" : "Assistent · svarar på nynorsk"}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <a href={COMPANY.phoneHref} className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-[background-color,color] duration-150 hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={`Ring ${COMPANY.phone}`} title={`Ring ${COMPANY.phone}`} data-testid="chat-call-link">
                  <Phone className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                </a>
                {!empty && (
                  <IconButton onClick={reset} aria-label="Start ny samtale" title="Ny samtale" data-testid="chat-reset-button">
                    <RotateCcw className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                  </IconButton>
                )}
                <IconButton onClick={() => setWide((w) => !w)} className="hidden sm:inline-flex" aria-label={wide ? "Gjer mindre" : "Gjer større"} title={wide ? "Mindre" : "Større"} aria-pressed={wide} data-testid="chat-expand-button">
                  {wide ? <Minimize2 className="h-4 w-4" strokeWidth={1.75} aria-hidden /> : <Maximize2 className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
                </IconButton>
                <IconButton onClick={close} aria-label="Lukk" data-testid="chat-close-button">
                  <X className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
                </IconButton>
              </div>
            </header>

            {/* Thread */}
            <div ref={listRef} onScroll={onScroll} className="scrollbar-thin relative flex-1 overflow-y-auto overscroll-contain px-5 pb-4 pt-[88px] sm:px-6" data-testid="chat-messages" aria-live="polite" aria-busy={busy}>
              {empty && <div className="chat-aura" aria-hidden />}
              <div className={cn("relative mx-auto flex min-h-full w-full flex-col", wide && "max-w-[640px]")}>
                {empty ? (
                  <div className="flex min-h-full flex-1 flex-col">
                    <motion.div className="pt-8 sm:pt-12" initial={reduce ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: EASE }}>
                      <p className="t-mono">{greeting()}</p>
                      <h2 className="mt-3 font-display text-[2rem] font-medium leading-[1.06] tracking-[-0.03em] sm:text-[2.25rem]">
                        Kva kan vi<br />hjelpe deg med?
                      </h2>
                      <p className="mt-4 max-w-[34ch] text-[15px] leading-relaxed text-muted-foreground">Still eit spørsmål om tenester og prisar, eller la assistenten registrere ei førespurnad for deg.</p>
                    </motion.div>
                    <div className="mt-auto pt-10">
                      <p className="text-[12.5px] font-medium text-muted-foreground">Prøv å spørje</p>
                      <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="chat-suggestions">
                        {SUGGESTIONS.map((s, i) => {
                          const Icon = s.icon;
                          return (
                            <motion.li key={s.label} initial={reduce ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05, duration: 0.32, ease: EASE }}>
                              <button
                                type="button"
                                onClick={() => send(s.text)}
                                className="group flex h-full w-full items-start gap-3 rounded-2xl border border-border bg-background/70 p-3.5 text-left transition-[border-color,background-color,transform] duration-150 hover:-translate-y-px hover:border-foreground/40 hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-col sm:gap-5"
                                data-testid={`chat-suggestion-${i}`}
                              >
                                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary text-foreground/70 transition-colors duration-150 group-hover:text-brand">
                                  <Icon className="h-[18px] w-[18px]" {...ICON} />
                                </span>
                                <span className="flex w-full flex-1 items-center justify-between gap-2">
                                  <span className="text-[14px] font-medium leading-snug text-foreground/90">{s.label}</span>
                                  <ArrowUpRight className="link-diag h-4 w-4 shrink-0 text-foreground/30 transition-colors duration-150 group-hover:text-foreground" {...ICON} />
                                </span>
                              </button>
                            </motion.li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-5">
                    {thread.map((row) => {
                      if (row.kind === "time") return <TimeDivider key={row.id} label={row.label} />;
                      const m = row.m;
                      if (m.role === "user") {
                        return (
                          <motion.div key={m.id} layout="position" initial={reduce ? false : { opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.26, ease: EASE }} className="flex justify-end" data-testid="chat-message-user">
                            <div className="max-w-[84%] whitespace-pre-wrap rounded-[20px] rounded-br-[6px] bg-foreground px-4 py-2.5 text-[15px] leading-relaxed text-background">{m.content}</div>
                          </motion.div>
                        );
                      }
                      const isLast = m.id === lastId;
                      const streaming = busy && isLast && !m.error && !m.stopped && !m.done;
                      const finished = !m.pending && !streaming && !m.error && !!m.content;
                      const services = finished ? detectServices(m.content) : [];
                      const card = services[0];
                      const more = services.slice(1, 3);
                      return (
                        <motion.div key={m.id} layout="position" initial={reduce ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.26, ease: EASE }} className="chat-turn max-w-[96%]" data-testid="chat-message-assistant">
                          {m.steps?.length > 0 && (
                            <div className="mb-2 flex flex-wrap gap-1.5">{m.steps.map((s) => <Step key={s.id} step={s} />)}</div>
                          )}
                          {m.pending && !m.content ? (
                            <p className="chat-shimmer text-[15px] font-medium" data-testid="chat-typing">Tenkjer …</p>
                          ) : (
                            m.content && <Reply text={m.content} streaming={streaming} />
                          )}
                          {m.inquiry?.ref && <InquiryReceipt inquiry={m.inquiry} />}
                          {finished && !m.inquiry && card && <ServiceCard item={card} onOrder={order} />}
                          {finished && (more.length > 0 || m.followups?.length > 0) && (
                            <div className="mt-3 flex flex-wrap gap-2" data-testid="chat-followups">
                              {more.map((s) => <Chip key={s.id} icon={SERVICE_ICONS[s.id] || Wrench} onClick={() => order(s)} testid={`chat-action-order-${s.id}`}>Bestill {s.name.toLowerCase()}</Chip>)}
                              {isLast && (m.followups || []).map((f, i) => (
                                <motion.span key={f} initial={reduce ? false : { opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05, duration: 0.25, ease: EASE }}>
                                  <Chip onClick={() => send(f)} testid={`chat-followup-${i}`}>{f}</Chip>
                                </motion.span>
                              ))}
                            </div>
                          )}
                          {finished && <TurnActions text={m.content} vote={m.vote} onVote={(v) => vote(m, v)} />}
                          {m.stopped && (
                            <p className="mt-2 flex items-center gap-2 text-[12.5px] text-muted-foreground" data-testid="chat-stopped">
                              Stoppa.
                              {!m.content && <button type="button" onClick={() => retry(m.id)} className="u-link font-medium text-foreground" data-testid="chat-retry-button">Prøv igjen</button>}
                            </p>
                          )}
                          {m.error && (
                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-border bg-secondary/60 px-3.5 py-2.5 text-[13.5px] leading-snug text-foreground" role="alert" data-testid="chat-error">
                              <span className="min-w-0 flex-1">{m.error} Ring gjerne <a href={COMPANY.phoneHref} className="u-link tabular font-medium">{COMPANY.phone}</a>.</span>
                              <button type="button" onClick={() => retry(m.id)} className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full bg-foreground px-3 text-[12.5px] font-medium text-background transition-transform duration-150 active:scale-95" data-testid="chat-retry-button">
                                <RotateCcw className="h-3 w-3" strokeWidth={2} aria-hidden /> Prøv igjen
                              </button>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Jump to the newest reply */}
            <AnimatePresence>
              {!atBottom && !empty && (
                <motion.button
                  key="to-bottom"
                  type="button"
                  onClick={() => { setAtBottom(true); scrollToBottom(true); }}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.18, ease: EASE }}
                  className="shadow-card absolute bottom-[118px] left-1/2 z-10 inline-flex h-8 -translate-x-1/2 items-center gap-1.5 rounded-full border border-border bg-background/90 px-3 text-[12.5px] font-medium text-foreground backdrop-blur-md transition-colors duration-150 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="chat-scroll-bottom"
                >
                  <ArrowDown className="h-3.5 w-3.5" strokeWidth={2} aria-hidden /> {busy ? "Svaret kjem" : "Til botnen"}
                </motion.button>
              )}
            </AnimatePresence>

            {/* Composer */}
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="chat-glass relative z-10 border-t border-border/60 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-4 sm:pb-4">
              <div className={cn("mx-auto w-full", wide && "max-w-[640px]")}>
                <div className="flex items-end gap-2 rounded-[24px] border border-input bg-card py-1.5 pl-4 pr-1.5 transition-[border-color,box-shadow] duration-150 focus-within:border-foreground/35 focus-within:shadow-[0_0_0_4px_hsl(var(--ink)/0.05)]">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={1}
                    placeholder={empty ? "Skriv spørsmålet ditt …" : "Skriv eit svar …"}
                    className="min-h-[36px] flex-1 resize-none bg-transparent py-2 text-[15px] leading-relaxed outline-none placeholder:text-muted-foreground/60"
                    data-testid="chat-input"
                    aria-label="Melding til assistenten"
                  />
                  {busy ? (
                    <button type="button" onClick={stop} className="mb-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-transform duration-150 hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Stopp svaret" title="Stopp" data-testid="chat-stop-button">
                      <Square className="h-3.5 w-3.5 fill-current" strokeWidth={2} aria-hidden />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={!canSend}
                      className={cn(
                        "mb-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-[background-color,color,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        canSend ? "bg-brand text-on-brand hover:scale-105 active:scale-95" : "bg-secondary text-foreground/35"
                      )}
                      aria-label="Send"
                      data-testid="chat-send-button"
                    >
                      <ArrowUp className="h-4 w-4" strokeWidth={2.25} aria-hidden />
                    </button>
                  )}
                </div>
                <div className="mt-2 flex items-center justify-between gap-3 px-1.5 text-[12px] text-muted-foreground">
                  <span className="truncate">
                    Ring <a href={COMPANY.phoneHref} className="u-link tabular font-medium text-foreground">{COMPANY.phone}</a>
                    <span aria-hidden> · </span>
                    <a href={formHref} onClick={(e) => { e.preventDefault(); go(formHref); }} className="u-link font-medium text-foreground" data-testid="chat-form-link">{empty ? "Opne skjema" : "Hald fram i skjema"}</a>
                  </span>
                  <span className="hidden shrink-0 sm:inline">Enter sender · Shift+Enter ny linje</span>
                </div>
              </div>
            </form>
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
};
